// Provider keys for the dashboard's provider filter (2026-09-30).
//
// A dashboard row names its provider in linked_provider, falling back to
// providername. Those strings disagree for the same person: the Athena sync and
// the Select Provider modal store the provider_master short name ("Dr. Madhu"),
// while patient-app bookings and reschedules stored the full Athena name
// ("Madhu Pamganamamula, MD"). Filtering on the raw string listed one provider
// twice and split their patients between the two entries.
//
// providerKey() maps every row to ONE name:
//   1. linked_provider that is a provider_master short name (trimmed, any
//      letter case - older rows carry "jaime" / "oco") wins, in the
//      provider_master spelling: it is either the provider's own name or a
//      deliberate staff reassignment made through the Select Provider modal,
//      which must keep showing under the reassigned provider;
//   2. otherwise the provider_master short name for the row's providerid;
//   3. otherwise the raw string, as before.
//
// Pure module (no api import) so it can be checked with plain node:
//   node --no-warnings src/utils/providerDirectory.test.mjs

export const EMPTY_PROVIDER_DIRECTORY = Object.freeze({
  names: new Set(),
  byId: new Map(),
  byLowerName: new Map(),
});

// GET /v1/{practiceid}/providers body.data: [{ providerid, providername }],
// where providerid is provider_master.id (what appointment rows carry).
export function buildProviderDirectory(list) {
  const names = new Set();
  const byId = new Map();
  const byLowerName = new Map(); // lower-cased name -> provider_master spelling
  (Array.isArray(list) ? list : []).forEach((p) => {
    const name = String(p?.providername ?? "").trim();
    if (!name) return;
    names.add(name);
    if (!byLowerName.has(name.toLowerCase())) byLowerName.set(name.toLowerCase(), name);
    const id = p?.providerid;
    if (id !== undefined && id !== null && id !== "") byId.set(String(id), name);
  });
  return { names, byId, byLowerName };
}

// The raw provider string the dashboard used before (unchanged precedence).
export function rawProviderName(row) {
  return (
    row?.linked_provider ||
    row?.providername ||
    row?.provider ||
    row?.provider_name ||
    [row?.providerfirstname, row?.providerlastname].filter(Boolean).join(" ") ||
    ""
  ).trim();
}

export function providerKey(row, directory = EMPTY_PROVIDER_DIRECTORY) {
  const dir = directory || EMPTY_PROVIDER_DIRECTORY;
  const linked = String(row?.linked_provider ?? "").trim();
  const named = linked && dir.byLowerName?.get(linked.toLowerCase());
  if (named) return named;
  const id = row?.providerid;
  if (id !== undefined && id !== null && id !== "") {
    const name = dir.byId.get(String(id));
    if (name) return name;
  }
  return rawProviderName(row);
}

// One providers request per practice per page load, shared by every caller.
// A failed load is forgotten, so the next caller (next mount) retries.
const directoryCache = new Map();

export function loadProviderDirectory(practiceid, fetchList) {
  const key = String(practiceid ?? "");
  if (!directoryCache.has(key)) {
    const pending = Promise.resolve()
      .then(() => fetchList(key))
      .then((res) => {
        // 200 with the list, or 201 with [] when the practice has none.
        if (!Array.isArray(res?.data)) {
          throw new Error("providers: unexpected response");
        }
        return buildProviderDirectory(res.data);
      })
      .catch((error) => {
        directoryCache.delete(key);
        throw error;
      });
    directoryCache.set(key, pending);
  }
  return directoryCache.get(key);
}
